
import { AbstractDestroyable, destroySafe } from '@deqo/foundation';
import { uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { TopicManager } from '../../../concepts/objects/TopicManager';
import { TopicSubscription } from '../../../concepts/objects/TopicSubscription';
import { EditorSession } from '../../canvas/EditorSession';
import { MyRoleInfo } from '../../canvas/MyRoleInfo';
import { resolve } from '../../Container';
import { ProfileModel } from '../../ProfileModel';
import { AlertHandle, AlertManager } from '../alerts/AlertManager';
import { LivePollState, PollSpec } from './PollTypes';
import { createPollView } from './ui/PollView';

export class PollController extends AbstractDestroyable {

    @resolve(ProfileModel) private me!: ProfileModel;
    @resolve(AlertManager) private alerts!: AlertManager;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(TopicManager) private topics!: TopicManager;

    private alert = null as AlertHandle;
    private topic = null as TopicSubscription<LivePollState>;

    private get isPollActive() {
        return !!this.topic.getState().active;
    }

    protected start(): void {
        this.burden(() => destroySafe(this.topic));
        this.subscribe();
    }

    public getPollState() {
        const state = this.topic.getState();
        return state?.active ? state : null;
    }

    public startFromSpec(spec: PollSpec) {
        this.topic.reset({ id: uuid(), active: true, ...spec });
    }

    public respondWith(answers: number[]) {
        if (this.isPollActive) {
            this.topic.patch({ [this.me.id]: answers });
        }
    }

    public finish() {
        this.topic.reset({});
    }

    private subscribe = async () => {
        this.topic = await this.topics.subscribe<LivePollState>('poll');
        this.topic.observe(this.onPollStateChanged);
        if (this.isPollActive) {
            this.show();
        }
    };

    private onPollStateChanged = (next: LivePollState, prev: LivePollState) => {
        // Do nothing if not able to participate
        const { canParticipate } = this.session.get(MyRoleInfo);
        if (!canParticipate) return;

        // If id changed or was inactive, show alert
        if (!!next.active && (next.id !== prev.id || !prev.active)) {
            this.show();
        }
        else if (!next.active) {
            this.hide();
        }
    };

    private show = () => {
        this.alert = this.alerts.invoke({
            type: 'dialog',
            body: createPollView(this.topic),
            delay: 0,
            modal: true,
        });
    };

    private hide = () => {
        if (this.alert) {
            this.alert.close();
        }
    };
}
